import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { 
  ChevronLeft, ChevronRight, Thermometer, Droplets, Wind, 
  Gauge, Volume2, Sun, LogIn, UserPlus, ArrowRight, 
  Shield, Zap, TrendingUp, CheckCircle, Play
} from "lucide-react";

export default function Cover() {
    const navigate = useNavigate();
    const [currentIndex, setCurrentIndex] = useState(0);
    const [isPaused, setIsPaused] = useState(false);
    const autoSlideRef = useRef(null);

    // Data parameter lingkungan
    const parameters = [
        {
            id: 1,
            nama: "Suhu",
            icon: <Thermometer className="w-10 h-10" />,
            color: "from-red-500 to-orange-500",
            fungsi: "Mengukur derajat panas atau dinginnya udara di dalam kelas secara real-time.",
            dampak: "Suhu ideal meningkatkan konsentrasi dan kenyamanan belajar. Suhu ekstrem menyebabkan kelelahan dan penurunan fokus.",
        },
        {
            id: 2,
            nama: "Kelembapan",
            icon: <Droplets className="w-10 h-10" />,
            color: "from-blue-500 via-cyan-500 to-sky-500",
            fungsi: "Mengukur kadar uap air di udara yang memengaruhi kenyamanan pernapasan.",
            dampak: "Kelembapan optimal mencegah penyebaran virus dan menjaga kesehatan saluran pernapasan.",
        },
        {
            id: 3,
            nama: "Asap",
            icon: <Wind className="w-10 h-10" />,
            color: "from-gray-600 via-slate-700 to-zinc-900",
            fungsi: "Mendeteksi asap rokok, kebakaran, atau polusi udara di ruangan.",
            dampak: "Mencegah gangguan pernapasan dan kenyamanan, serta memberikan peringatan dini bahaya kebakaran.",
        },
        {
            id: 4,
            nama: "Gas CO₂",
            icon: <Gauge className="w-10 h-10" />,
            color: "from-emerald-500 to-teal-600",
            fungsi: "Mengukur kadar karbon dioksida hasil pernapasan penghuni ruangan.",
            dampak: "Ventilasi yang baik menjaga konsentrasi siswa. Kadar tinggi menyebabkan kantuk dan penurunan kognitif.",
        },
        {
            id: 5,
            nama: "Kebisingan",
            icon: <Volume2 className="w-10 h-10" />,
            color: "from-violet-500 via-purple-500 to-indigo-600",
            fungsi: "Mengukur tingkat kebisingan dari aktivitas di dalam dan luar kelas.",
            dampak: "Lingkungan tenang di bawah 40 dB dapat meningkatkan fokus dan kenyamanan belajar, serta mengurangi stres siswa.",
        },
        {
            id: 6,
            nama: "Intensitas Cahaya",
            icon: <Sun className="w-10 h-10" />,
            color: "from-amber-500 via-orange-500 to-rose-500",
            fungsi: "Mengukur tingkat kecerahan pencahayaan di ruang kelas.",
            dampak: "Cahaya ideal mengurangi kelelahan mata, sakit kepala, penurunan konsentrasi, serta mendukung aktivitas membaca dan menulis.",
        },
    ];

    useEffect(() => {
        startAutoSlide();
        return () => clearInterval(autoSlideRef.current);
    }, []);

    const startAutoSlide = () => {
        clearInterval(autoSlideRef.current);
        autoSlideRef.current = setInterval(() => {
            setCurrentIndex((prev) => (prev + 1) % parameters.length);
        }, 4500);
    };

    const pauseAutoSlide = () => {
        setIsPaused(true);
        clearInterval(autoSlideRef.current);
    };

    const resumeAutoSlide = () => {
        setIsPaused(false);
        startAutoSlide();
    };

    const nextSlide = () => {
        setCurrentIndex((prev) => (prev + 1) % parameters.length);
    };

    const prevSlide = () => {
        setCurrentIndex((prev) => (prev - 1 + parameters.length) % parameters.length);
    };

    return (
        <div className="min-h-screen bg-gray-50 text-zinc-800 overflow-x-hidden">
            {/* Navbar */}
            <nav className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-lg border-b border-gray-200 shadow-sm">
                <div className="max-w-7xl mx-auto px-6 py-5 flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <button 
                            onClick={() => navigate("/login")}
                            className="flex items-center gap-2 px-6 py-2.5 rounded-2xl border border-emerald-300 hover:bg-emerald-50 transition-all text-sm font-medium"
                        >
                            <LogIn className="w-4 h-4" />
                            Masuk
                        </button>
                        <button 
                            onClick={() => navigate("/signup")}
                            className="flex items-center gap-2 px-6 py-2.5 bg-emerald-600 text-white rounded-2xl font-semibold hover:bg-emerald-500 transition-all shadow-lg shadow-emerald-500/30"
                        >
                            Daftar
                            <ArrowRight className="w-4 h-4" />
                        </button>
                    </div>
                </div>
            </nav>

            {/* HERO SECTION */}
            <div className="pt-24 pb-20 md:pt-32 md:pb-28 relative overflow-hidden">
                <div className="absolute inset-0 bg-[radial-gradient(at_50%_30%,rgba(16,185,129,0.15),transparent)]"/>
                <div className="max-w-5xl mx-auto px-6 text-center relative">
                    <h1 className="text-5xl md:text-7xl font-bold tracking-tighter leading-tight mb-6">
                        Sistem Pemantauan Lingkungan<br/>
                        <span className="bg-gradient-to-r from-emerald-400 via-teal-300 to-cyan-400 bg-clip-text text-transparent">Ruang Kelas Cerdas & Sehat</span>
                    </h1>

                    <p className="text-xl text-gray-600 max-w-2xl mx-auto mb-10">
                        Pantau kualitas lingkungan ruang belajar secara real-time dengan teknologi IoT dan Machine Learning. 
                        Ciptakan suasana belajar optimal untuk meningkatkan konsentrasi dan kesehatan siswa.
                    </p>

                    <div className="flex flex-col sm:flex-row gap-4 justify-center">
                        <button onClick={() => document.getElementById('parameters').scrollIntoView({ behavior: 'smooth' })}
                            className="px-10 py-4 bg-emerald-600 hover:bg-emerald-500 rounded-2xl font-semibold text-lg text-white flex items-center justify-center gap-3 group transition-all"
                        >
                            Lihat Parameter
                            <ArrowRight className="group-hover:translate-x-1 transition" />
                        </button>
                        <button onClick={() => navigate("/dashboard")}
                            className="px-10 py-4 border border-emerald-300 hover:bg-emerald-100 rounded-2xl font-semibold text-lg transition-all"
                        >
                            Mulai Sekarang
                        </button>
                    </div>

                    {/* Trust signals */}
                    <div className="flex items-center justify-center gap-8 mt-12 text-gray-500 text-sm">
                        <div className="flex items-center gap-2">
                            <Shield className="w-5 h-5" /> Aman
                        </div>
                        <div className="flex items-center gap-2">
                            <TrendingUp className="w-5 h-5" /> Real-time
                        </div>
                        <div className="flex item-center gap-2">
                            <CheckCircle className="w-5 h-5"/>Terintegrasi IoT & Machine Learning
                        </div>
                    </div>
                </div>
            </div>

            {/* PARAMETERS SECTION */}
            <div id="parameters" className="py-20 bg-white">
                <div className="max-w-7xl mx-auto px-6">
                    <div className="text-center mb-12">
                        <h2 className="text-4xl font-bold tracking-tight mb-3 text-gray-900">Parameter Lingkungan</h2>
                        <p className="text-gray-600 text-lg max-w-md mx-auto">
                            Sistem memantau 6 faktor utama yang memengaruhi kualitas belajar
                        </p>
                    </div>

                    <div className="relative group" onMouseEnter={pauseAutoSlide} onMouseLeave={resumeAutoSlide}>
                        {/* Slider Container */}
                        <div className="overflow-hidden rounded-3xl shadow-2xl border border-gray-100">
                            <div 
                                className="flex transition-transform duration-700 ease-out" 
                                style={{ transform: `translateX(-${currentIndex * 100}%)` }}
                            >
                                {parameters.map((param, idx) => (
                                    <div key={param.id} className="w-full flex-shrink-0">
                                        <div className={`bg-gradient-to-br ${param.color} h-[520px] md:h-[560px] relative flex items-center overflow-hidden`}>
                                            {/* Background Effects */}
                                            <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_40%,rgba(255,255,255,0.25),transparent_60%)]" />
                                            
                                            <div className="relative z-10 max-w-4xl mx-auto px-8 md:px-16 text-center">
                                                <div className="inline-flex items-center justify-center w-20 h-20 bg-white/10 backdrop-blur-xl rounded-3xl mb-8 shadow-xl border border-white/20 text-white/90">
                                                    {param.icon}
                                                </div>
                                                
                                                <h3 className="text-5xl md:text-6xl font-bold mb-6 tracking-tighter">
                                                    {param.nama}
                                                </h3>
                                                
                                                <div className="max-w-2xl mx-auto space-y-8">
                                                    <div>
                                                        <div className="uppercase tracking-widest text-xs text-white/60 mb-2">FUNGSI SENSOR</div>
                                                        <p className="text-lg md:text-xl leading-relaxed text-white/90">
                                                            {param.fungsi}
                                                        </p>
                                                    </div>
                                                    
                                                    <div>
                                                        <div className="uppercase tracking-widest text-xs text-white/60 mb-2">DAMPAK TERHADAP PEMBELAJARAN</div>
                                                        <p className="text-lg md:text-xl leading-relaxed text-white/90">
                                                            {param.dampak}
                                                        </p>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Navigation Buttons */}
                        <button onClick={prevSlide}
                            className="absolute left-6 top-1/2 -translate-y-1/2 bg-white/40 hover:bg-white/70 backdrop-blur-md p-3 rounded-2xl border border-gray-200 transition-all opacity-0 group-hover:opacity-100"
                        >
                            <ChevronLeft className="w-4 h-4" />
                        </button>
                        
                        <button onClick={nextSlide}
                            className="absolute right-6 top-1/2 -translate-y-1/2 bg-white/40 hover:bg-white/70 backdrop-blur-md p-3 rounded-2xl border border-gray-200 transition-all opacity-0 group-hover:opacity-100"
                        >
                            <ChevronRight className="w-4 h-4" />
                        </button>

                        {/* Progress Dots */}
                        <div className="flex justify-center gap-3 mt-8">
                            {parameters.map((_, idx) => (
                                <button
                                    key={idx}
                                    onClick={() => setCurrentIndex(idx)}
                                    className={`h-2.5 rounded-full transition-all duration-300 ${
                                        currentIndex === idx 
                                        ? 'w-10 bg-gradient-to-r from-emerald-500 to-cyan-400' 
                                        : 'w-2.5 bg-gray-300 hover:bg-gray-400'
                                    }`}
                                />
                            ))}
                        </div>
                    </div>
                </div>
            </div>

            {/* BENEFITS SECTION */}
            <div id="manfaat" className="py-20 bg-gray-100">
                <div className="max-w-6xl mx-auto px-6">
                    <div className="grid md:grid-cols-3 gap-8">
                        {[
                            {
                                icon: <Shield className="w-8 h-8" />,
                                title: "Kesehatan Terjaga",
                                desc: "Kurangi risiko penyakit pernapasan dan kelelahan siswa melalui pemantauan lingkungan yang akurat."
                            },
                            {
                                icon: <TrendingUp className="w-8 h-8" />,
                                title: "Prestasi Meningkat",
                                desc: "Lingkungan optimal terbukti meningkatkan konsentrasi, daya ingat, dan hasil belajar siswa."
                            },
                            {
                                icon: <Zap className="w-8 h-8" />,
                                title: "Manajemen Cepat",
                                desc: "Notifikasi real-time dan dashboard yang memudahkan guru & kepala sekolah mengambil keputusan."
                            }
                        ].map((benefit, i) => (
                            <div key={i} className="bg-white/50 border border-gray-100 rounded-3xl p-10 hover:border-emerald-500/40 shadow-2xl shadow-emerald-300/20 transition-all group">
                                <div className="text-emerald-400 mb-6 group-hover:scale-110 transition-transform">
                                    {benefit.icon}
                                </div>
                                <h3 className="text-2xl font-semibold mb-4">{benefit.title}</h3>
                                <p className="text-gray-600 leading-relaxed">{benefit.desc}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* VIDEO PANDUAN DASHBOARD */}
            <div className="py-24 bg-white border-t border-gray-100">
                <div className="max-w-5xl mx-auto px-6 text-center">
                    <div className="mb-12">
                        <h2 className="text-4xl font-bold tracking-tight mb-4 text-gray-900">
                            Panduan Penggunaan Dashboard
                        </h2>
                        <p className="text-gray-600 text-lg max-w-md mx-auto">
                            Pelajari cara menggunakan sistem pemantauan lingkungan ruang kelas dalam waktu kurang dari 10 menit
                        </p>
                    </div>

                    <div className="relative mx-auto max-w-4xl rounded-3xl overflow-hidden shadow-2xl border border-gray-200 bg-gray-900 aspect-video group">
                        <video
                            className="w-full h-full object-cover"
                            poster="/video-poster.png"
                            controls
                            preload="metadata"
                        >
                            <source 
                                src="/panduan-dashboard.mp4" 
                                type="video/mp4" />
                            Browser Anda tidak mendukung pemutaran video.
                        </video>

                        {/* Overlay Play Button (muncul sebelum video diputar) */}
                        {/* <div className="absolute inset-0 flex items-center justify-center pointer-events-none group-hover:opacity-0 transition-opacity duration-300">
                            <div className="w-20 h-20 bg-white/90 backdrop-blur-md rounded-full flex items-center justify-center shadow-xl">
                                <Play className="w-10 h-10 text-emerald-600 ml-1" />
                            </div>
                        </div> */}
                    </div>

                    <p className="text-gray-500 text-md mt-6">
                        Video ini menjelaskan fitur utama, cara membaca data sensor, dan cara mendapatkan notifikasi
                    </p>
                </div>
            </div>

            {/* Footer */}
            <footer className="bg-black border-t border-white/10 py-12 text-center text-zinc-500 text-sm">
                <p>© 2026 Sistem Pemantauan Lingkungan Ruang Kelas • IoT & Machine Learning</p>
            </footer>
        </div>
    );
}